import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from 'react-bootstrap';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import EstatusCfdiModal from './EstatusCfdiModal';

const parseXmlToObject = (xmlString) => {
  try {
    const parser = new window.DOMParser();
    const xml = parser.parseFromString(xmlString, 'text/xml');
    const get = (tag) => xml.getElementsByTagName(tag)[0]?.textContent || '';
    return {
      consultaOk: get('consultaOk'),
      codigo: get('codigo'),
      codigoEstatus: get('codigoEstatus'),
      esCancelable: get('esCancelable'),
      estado: get('estado'),
      estatusCfdi: get('estatusCfdi')
    };
  } catch {
    return null;
  }
};

const ConsultarEstatus = ({ uuid, rfcEmisor, rfcReceptor, total }) => {
  const { token, setToken } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [modalInfo, setModalInfo] = useState(null);

  const normalizeTotal = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') return val.toString();
    let s = String(val).trim();
    // Remove currency symbols, letters and spaces
    s = s.replace(/[^0-9.,-]/g, '');
    if (s === '') return null;
    // Remove thousand separators (commas) and keep dot as decimal separator
    // e.g. "$1,234.56" -> "1234.56" ; "1.234,56" unlikely but handle common case by replacing commas
    s = s.replace(/,/g, '');
    return s;
  };

  const handleClick = async () => {
    const normalizedTotal = normalizeTotal(total);

    if (!uuid || !rfcEmisor || !rfcReceptor || !normalizedTotal) {
      showErrorToast('Faltan datos para consultar estatus.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        uuid,
        rfcEmisor,
        rfcReceptor,
        total: normalizedTotal
      };

      const response = await fetch('/api/contabilidad/Facturacion/ConsultarStatus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);
        if (data?.detalles?.rawResponse) {
          const info = parseXmlToObject(data.detalles.rawResponse);
          setModalInfo(info);
        } else {
          showSuccessToast(data?.vMessage || 'Consulta de estatus completada.');
        }
      } else {
        if (response.status === 401) {
          setToken(null);
          showErrorToast('No autorizado.');
          return;
        }
        const err = await response.json().catch(() => null);
        showErrorToast(err?.vMessage || `Error al consultar estatus: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      showErrorToast('Error al conectar con el servidor: ' + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="primary" onClick={handleClick} disabled={loading} className="px-4 text-white">
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" /> Consultando...
          </>
        ) : (
          <>
            <i className="fas fa-search me-2"></i>Consultar estatus
          </>
        )}
      </Button>
      <EstatusCfdiModal show={!!modalInfo} onHide={() => setModalInfo(null)} info={modalInfo} />
    </>
  );
};

ConsultarEstatus.propTypes = {
  uuid: PropTypes.string,
  folio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rfcEmisor: PropTypes.string,
  rfcReceptor: PropTypes.string,
  total: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ConsultarEstatus;
